import { useState, useEffect } from "react";
import { getBeneficiaryDashboard, getBeneficiaryPayments } from "../services/api";
import LoanDashboard from "../components/LoanDashboard";

function Badge({ label, color, bg }) {
  return <span style={{ background:bg||"#d1fae5", color:color||"#065f46", borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700 }}>{label}</span>;
}

export default function Dashboard({ userData, onLogout }) {
  const [tab, setTab]           = useState("overview");
  const [data, setData]         = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dash, pays] = await Promise.all([getBeneficiaryDashboard(), getBeneficiaryPayments()]);
        setData(dash); setPayments(pays);
      } catch(err) {
        setData({ fullName: userData?.fullName || userData?.email?.split("@")[0], email: userData?.email });
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const name = data?.fullName || userData?.fullName || userData?.email?.split("@")[0] || "Beneficiary";

  if (loading) return <div style={{ textAlign:"center", padding:80, color:"var(--text-secondary)" }}>Loading your dashboard...</div>;

  const tabs = [
    ["overview","Overview"],
    ["loan","My Loan"],
    ["payments","Payments"],
    ["profile","Profile"],
    ["support","Support"],
  ];

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg-light)" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#0066cc,#0052a3)", color:"white", padding:"clamp(16px,4vw,28px) clamp(16px,4vw,40px)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
        <div>
          <p style={{ margin:"0 0 4px", fontSize:13, opacity:.8 }}>Welcome back,</p>
          <h1 style={{ margin:"0 0 6px", fontSize:24, fontWeight:800 }}>{name}</h1>
          <div style={{ display:"flex", gap:8 }}>
            <Badge label={data?.category||"Loan Client"} color="#0284c7" bg="#e0f2fe"/>
            <Badge label="Active" color="#047857" bg="#d1fae5"/>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <p style={{ margin:"0 0 4px", fontSize:12, opacity:.75 }}>Account Balance</p>
          <p style={{ margin:"0 0 6px", fontSize:22, fontWeight:800 }}>KSh {(data?.accountBalance||0).toLocaleString()}</p>
          <Badge label="Active Account" color="#92400e" bg="#fef3c7"/>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"clamp(12px,3vw,28px) clamp(12px,3vw,24px)" }}>
        {/* Tabs */}
        <div style={{ display:"flex", gap:4, background:"white", borderRadius:10, padding:4, marginBottom:24, boxShadow:"0 1px 6px rgba(0,0,0,0.07)", overflowX:"auto" }}>
          {tabs.map(([id,lbl])=>(
            <button key={id} onClick={()=>setTab(id)} style={{ flex:1, padding:"10px 8px", borderRadius:7, border:"none", cursor:"pointer", fontSize:13, minWidth:80,
              background:tab===id?"var(--primary-color)":"transparent",
              color:tab===id?"white":"var(--text-secondary)",
              fontWeight:tab===id?700:500 }}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab==="overview" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:16, marginBottom:24 }}>
              {[["Status","Active Beneficiary","#0066cc","#0066cc"],["Total Received","KSh "+(data?.totalReceived||0).toLocaleString(),"#10b981","#10b981"],["Payments Made",(data?.paymentCount||0)+" payments","#8b5cf6","#8b5cf6"],["Last Payment",data?.lastPaymentDate?.split("T")[0]||"N/A","#f59e0b","#f59e0b"]].map(([label,value,color,top])=>(
                <div key={label} style={{ background:"white", padding:20, borderRadius:12, borderTop:"4px solid "+top, boxShadow:"0 2px 8px rgba(0,0,0,0.07)" }}>
                  <p style={{ color:"var(--text-secondary)", margin:"0 0 8px", fontSize:13 }}>{label}</p>
                  <h3 style={{ fontSize:18, fontWeight:700, margin:0, color }}>{value}</h3>
                </div>
              ))}
            </div>
            <div style={{ background:"white", padding:24, borderRadius:12, boxShadow:"0 2px 8px rgba(0,0,0,0.07)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <h2 style={{ fontSize:16, fontWeight:700, margin:0 }}>Recent Transactions</h2>
                <button onClick={()=>setTab("payments")} style={{ background:"none", border:"none", color:"var(--primary-color)", fontWeight:700, fontSize:13, cursor:"pointer" }}>View all</button>
              </div>
              {payments.slice(0,4).map((p,i)=>(
                <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:"1px solid var(--border-light)" }}>
                  <div>
                    <p style={{ margin:"0 0 3px", fontWeight:600, fontSize:14 }}>Loan Disbursement</p>
                    <p style={{ margin:0, fontSize:12, color:"var(--text-light)" }}>{p.createdAt?.split("T")[0]||"�"}</p>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontWeight:700, color:"#10b981" }}>+KSh {(p.amount||0).toLocaleString()}</div>
                    <Badge label={p.status||"Completed"}/>
                  </div>
                </div>
              ))}
              {!payments.length && <p style={{ textAlign:"center", color:"var(--text-light)", padding:20 }}>No transactions yet</p>}
            </div>
          </>
        )}

        {/* My Loan */}
        {tab==="loan" && (
          <div>
            {data?.selectedLoanAmount && (
              <div style={{ background:"white", borderRadius:14, padding:"20px 24px", marginBottom:16, boxShadow:"0 2px 8px rgba(0,0,0,0.07)", border:"1px solid #e2e8f0" }}>
                <p style={{ fontSize:11, fontWeight:700, color:"#94a3b8", margin:"0 0 12px", textTransform:"uppercase", letterSpacing:1 }}>Loan Summary</p>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12 }}>
                  {[["Loan Amount","KSh "+(data.selectedLoanAmount||0).toLocaleString(),"#0066cc"],["Processing Fee (15%)","KSh "+Math.ceil((data.selectedLoanAmount||0)*0.15).toLocaleString(),"#ef4444"],["Amount to Receive","KSh "+Math.ceil((data.selectedLoanAmount||0)*0.85).toLocaleString(),"#10b981"],["Loan Status",data.loanStatus||"Pending","#f59e0b"]].map(([k,v,c])=>(
                    <div key={k} style={{ background:"#f8fafc", borderRadius:10, padding:"14px 16px", border:"1px solid #e2e8f0" }}>
                      <p style={{ fontSize:11, color:"#94a3b8", fontWeight:600, margin:"0 0 5px", textTransform:"uppercase", letterSpacing:.5 }}>{k}</p>
                      <p style={{ fontSize:16, fontWeight:800, margin:0, color:c }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <LoanDashboard applicationId={data?.applicationId || 1} phone={data?.phone || userData?.phone || "0711913399"} />
          </div>
        )}

        {/* Payments */}
        {tab==="payments" && (
          <div style={{ background:"white", padding:28, borderRadius:12, boxShadow:"0 2px 8px rgba(0,0,0,0.07)" }}>
            <h2 style={{ fontSize:18, fontWeight:700, marginBottom:20 }}>Payment History</h2>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead><tr style={{ background:"var(--bg-light)" }}>
                {["Date","Description","Amount","Status"].map(h=><th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"var(--text-secondary)", fontSize:11 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {payments.length>0 ? payments.map((p,i)=>(
                  <tr key={i} style={{ borderBottom:"1px solid var(--border-light)" }}>
                    <td style={{ padding:"12px 14px", color:"var(--text-secondary)" }}>{p.createdAt?.split("T")[0]||"�"}</td>
                    <td style={{ padding:"12px 14px", fontWeight:600 }}>Loan Disbursement</td>
                    <td style={{ padding:"12px 14px", fontWeight:700, color:"#10b981" }}>+KSh {(p.amount||0).toLocaleString()}</td>
                    <td style={{ padding:"12px 14px" }}><Badge label={p.status||"Completed"}/></td>
                  </tr>
                )) : <tr><td colSpan={4} style={{ padding:32, textAlign:"center", color:"var(--text-light)" }}>No payment records yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Profile */}
        {tab==="profile" && (
          <div style={{ background:"white", padding:28, borderRadius:12, boxShadow:"0 2px 8px rgba(0,0,0,0.07)" }}>
            <h2 style={{ fontSize:18, fontWeight:700, marginBottom:20 }}>My Profile</h2>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:16 }}>
              {[["Full Name",name],["Email",data?.email||userData?.email||"�"],["County",data?.county||"�"],["Category",data?.category||"�"],["Account Status","Active"],["Member Since",data?.createdAt?.split("T")[0]||"�"]].map(([label,value])=>(
                <div key={label} style={{ background:"var(--bg-light)", padding:16, borderRadius:10, border:"1px solid var(--border-light)" }}>
                  <p style={{ color:"var(--text-light)", fontSize:12, fontWeight:600, margin:"0 0 6px", textTransform:"uppercase", letterSpacing:"0.4px" }}>{label}</p>
                  <p style={{ fontSize:15, fontWeight:600, margin:0 }}>{value}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop:20, padding:"13px 15px", background:"#fff8e6", border:"1px solid #f59e0b30", borderRadius:8, fontSize:13, color:"#92400e" }}>
              To update your details, contact us at <strong>Toll-Free 1533</strong>.
            </div>
          </div>
        )}

        {/* Support */}
        {tab==="support" && (
          <div style={{ display:"grid", gap:14 }}>
            {[["Toll-Free Hotline","1533","Mon-Fri 8am-5pm"],["Email","inuajamii@socialprotection.go.ke","Response within 24-48 hours"],["WhatsApp","0700 000 000","Quick loan queries"],["Report Fraud","fraud@socialprotection.go.ke","Report suspicious activity"]].map(([title,value,desc])=>(
              <div key={title} style={{ background:"white", padding:"18px 22px", borderRadius:12, boxShadow:"0 2px 8px rgba(0,0,0,0.07)", display:"flex", gap:16, alignItems:"center" }}>
                <div style={{ flex:1 }}>
                  <p style={{ margin:"0 0 2px", fontWeight:700, fontSize:15 }}>{title}</p>
                  <p style={{ margin:"0 0 2px", fontWeight:600, fontSize:14, color:"var(--primary-color)" }}>{value}</p>
                  <p style={{ margin:0, fontSize:12, color:"var(--text-light)" }}>{desc}</p>
                </div>
                <button style={{ padding:"8px 16px", background:"transparent", color:"var(--primary-color)", border:"1.5px solid var(--primary-color)", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>Contact</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}






